import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box, Text } from '@primer/react';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Box sx={{
      fontSize: 3
    }} className="lead" as="p" mdxType="Box">
    Save patterns help users store and update their content and configuration throughout GitHub. These changes should be represented in the UI accurately, quickly, and obviously. Their behavior should inspire confidence and trust.
    </Box>
    <h2>{`Explicit saving`}</h2>
    <p>{`When designing a form, start with an explicit saving pattern. Avoid mixing explicit and automatic save patterns on a single page with multiple forms, and never mix save patterns in a single form. For example: if I upload a profile photo in the "Public profile" form, that change should not be saved until I explicitly submit the whole form.`}</p>
    <p>{`Explicitly saved forms can be saved by clicking a submit button or by pressing Enter while focused on an input.`}</p>
    <p>{`If there is an error saving the data, the user's data should be preserved in the form and they should be given `}<a parentName="p" {...{
        "href": "#feedback"
      }}>{`feedback`}</a>{` about the failure.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img alt="A form broken into two sections where one section has a dropdown that automatically saves" src="https://user-images.githubusercontent.com/2313998/159594852-aa1223c7-201e-42ea-9109-649371709d22.png" />
    <Caption mdxType="Caption">Separate auto-saving controls from an explicitly saved form</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img alt="A form with a single section and it contains a dropdown that automatically saves" src="https://user-images.githubusercontent.com/2313998/159594854-8edb9736-bdd1-436c-a327-8525b6ddac0c.png" />
    <Caption mdxType="Caption">Don't mix auto-saving controls and explicitly saved controls in the same form</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Unsaved changes`}</h3>
    <p>{`If a user tries to navigate away from a page with a form that has unsaved changes, you have the option to warn them that their changes will be lost using the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event"
      }}><inlineCode parentName="a">{`beforeunload`}</inlineCode>{` event`}</a>{`. Modern browsers prevent developers from customizing the `}<inlineCode parentName="p">{`alert`}</inlineCode>{` message, so we're limited to a generic message like "Do you want to leave this site? Changes you made may not be saved."`}</p>
    <h3>{`Declarative controls`}</h3>
    <p>{`To ensure your forms are accessible, you should use explicit saving (and not automatic saving) in declarative controls. These are:`}</p>
    <ul>
      <li parentName="ul">{`Text inputs`}</li>
      <li parentName="ul">{`Checkbox groups`}</li>
      <li parentName="ul">{`Radio button groups`}</li>
      <li parentName="ul">{`Browser-native `}<inlineCode parentName="li">{`<select>`}</inlineCode>{` dropdown menus`}</li>
      <li parentName="ul">{`Multi-select dropdown menus`}</li>
    </ul>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/2313998/158658672-8a503a7c-3857-4929-a77f-c975153e366c.png" />
    <Caption mdxType="Caption">Use save and cancel buttons in dropdowns that allow multiple selections</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/2313998/151623713-12028e6d-86ba-4ae6-b46b-d3c88c0fa2c0.png" />
    <Caption mdxType="Caption">Don't automatically save selections once the dropdown is closed</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`There are a few issues to bear in mind with autosaving declarative controls.`}</p>
    <ul>
      <li parentName="ul">{`Text inputs: Users expect to set a value and then submit. Sensitive information could be unintentionally submitted, such as a change password input.`}</li>
      <li parentName="ul">{`Checkbox groups: It could be unclear whether or not the selection has been saved. Users may accidentally submit a selection clicked by accident.`}</li>
      <li parentName="ul">{`Radio button groups: Screenreader users can't read the options without selecting them, which could accidentally apply a selection the user didn't want.`}</li>
      <li parentName="ul">{`Browser-native `}<inlineCode parentName="li">{`<select>`}</inlineCode>{` dropdowns: Similarly to radio button groups, options could be selected when a user focuses the select and uses the arrow keys to read through each option.`}</li>
    </ul>
    <h3>{`Calls to action`}</h3>
    <p>{`When data is not automatically saved after the user makes changes, buttons are used to submit or cancel the changes.`}</p>
    <h4>{`Text`}</h4>
    <p>{`All configuration flows should have calls to action that include an obvious active verb such as "Create", "Save", "Delete", or "Update" (for example, "Create" a new repository, "Add" an SSH key to your profile, "Save" security preferences, "Update" a repository's description, "Delete" an old email address).`}</p>
    <p>{`When defining these calls to actions, make sure to:`}</p>
    <ol>
      <li parentName="ol">{`Keep the text as succinct and clear as possible`}</li>
      <li parentName="ol">{`Add the item’s name to the button text in cases where it may be unclear to the user what is being changed`}</li>
    </ol>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/2313998/152432685-08d8a944-c802-44ed-89e0-e9d3aa87771b.png" />
    <Caption mdxType="Caption">Use descriptive text with an active verb</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/2313998/152432682-2e102919-114a-43ce-a446-5501d287084a.png" />
    <Caption mdxType="Caption">Don't use vague text</Caption>
  </Dont>
    </DoDontContainer>
    <h4>{`Appearance`}</h4>
    <ul>
      <li parentName="ul">{`If the save button is used to save every input on the page, use the `}<strong parentName="li">{`primary`}</strong>{` button appearance.`}</li>
      <li parentName="ul">{`If the save button refers to a segment of controls on the page, use the `}<strong parentName="li">{`secondary`}</strong>{` button appearance.`}</li>
      <li parentName="ul">{`If the save button has a corresponding cancel button, use the `}<strong parentName="li">{`primary`}</strong>{` button appearance for the save button, and `}<strong parentName="li">{`secondary`}</strong>{` for the cancel button.`}</li>
    </ul>
    <p>{`For more guidance on button usage, see the `}<a parentName="p" {...{
        "href": "/ui-patterns/button-usage"
      }}>{`button interface guidelines`}</a>{`.`}</p>
    <Box mb={3} display="flex" alignItems="flex-start" justifyContent="space-between" flexDirection={["column", "column", "column", "column", "row"]} sx={{
      gap: 3
    }} mdxType="Box">
    <Box mb={3} display="flex" alignItems="center" flexDirection="column" mx="auto" sx={{
        gap: 2
      }} mdxType="Box">
        <img width="456" src="https://user-images.githubusercontent.com/2313998/152432687-e119f44c-9325-4ae7-b277-e8559a4eb37b.png" />
        <Text as="p" mt="0" align="center" fontSize={1} mdxType="Text">Controls all belong to one form</Text>
        <img width="456" alt="A small form in a dialog that has 'Cancel' and 'Save' buttons at the bottom right" src="https://user-images.githubusercontent.com/2313998/151623724-d59d5967-5484-4e81-a4c2-1109409956ee.png" />
        <Text as="p" mt="0" align="center" fontSize={1} mdxType="Text">A form with a button to save and a button to cancel</Text>
    </Box>
    <Box mb={3} display="flex" alignItems="center" flexDirection="column" mx="auto" sx={{
        gap: 2
      }} mdxType="Box">
        <img width="456" src="https://user-images.githubusercontent.com/2313998/152432688-e74f67b2-a777-4c5f-be45-37a43615e6ab.png" />
        <Text as="p" mt="0" align="center" fontSize={1} mdxType="Text">Controls divided into multiple sections</Text>
    </Box>
    </Box>
    <h4>{`State`}</h4>
    <p>{`Do not disable or hide a form's save button even if the form is invalid or has not been changed. Disabled buttons cannot be focused using the Tab key, and disabled button styles are typically low-contrast and difficult to read.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/2313998/151623709-2a4257d6-e8d0-4156-b928-c7097981791c.png" />
    <Caption mdxType="Caption">Always keep the save button enabled</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/2313998/151623715-3b298004-b624-43dc-875c-2590c08070e7.png" />
    <Caption mdxType="Caption">Don't disable the save button if the form is invalid or unchanged</Caption>
  </Dont>
    </DoDontContainer>
    <h4>{`Placement`}</h4>
    <p>{`Place save buttons somewhere intuitive and easily accessible. `}</p>
    <ul>
      <li parentName="ul">{`There should only be one save button on a page.`}</li>
      <li parentName="ul">{`Place save buttons somewhere intuitive and easily accessible. If all of the fields save automatically, do not include a save button. This makes it unclear when data is being saved. See the guidelines on `}<a parentName="li" {...{
          "href": "#explicit-saving"
        }}>{`explicit saving`}</a>{` and `}<a parentName="li" {...{
          "href": "#automatic-saving"
        }}>{`automatic saving`}</a>{` for more information.`}</li>
      <li parentName="ul">{`Allow only one edit mode activated at a time if you're designing a page with content that can be edited separately (such as an issue title). Pages that contain multiple save buttons can cause confusion about which save button saves which group. Multiple save buttons with the same label can cause confusion for people who use assistive technologies like screen readers and voice recognition software.`}</li>
    </ul>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img alt="A form with one save button at the bottom" src="https://user-images.githubusercontent.com/2313998/151887934-cfcc5130-53a8-4f40-ba36-97148ead72c0.png" />
    <Caption mdxType="Caption">Have one save button per form</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img alt="A form with two sections. Each section has it's own save button." src="https://user-images.githubusercontent.com/2313998/151887938-1fa6b5d7-c5c1-4010-ab99-e5c37bff8c0a.png" />
    <Caption mdxType="Caption">Don't have one save button per section of a form</Caption>
  </Dont>
    </DoDontContainer>
    <h5>{`Bottom-left (default)`}</h5>
    <p>{`If there is a button to submit and a button to cancel, the cancel button should go to the `}<strong parentName="p">{`right`}</strong>{` of the submit button.`}</p>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={["column", "column", "column", "column", "row"]} sx={{
      gap: 3
    }} mdxType="Box">
    <img width="456" alt="A form with the controls and save button left aligned" src="https://user-images.githubusercontent.com/2313998/155623608-440c8f2c-dbb1-4b3c-a76e-7f01af63b3a8.png" />
    <Box as="p" mt="0" mdxType="Box">Default to placing the submit button at the bottom left of the form. Labels and inputs are left-aligned and run from top-to-bottom, so users would naturally move their eyes down and to the left.</Box>
    </Box>
    <h5>{`Bottom-right (dialogs and comments)`}</h5>
    <p>{`If there is a button to submit and a button to cancel, the button to cancel should go to the `}<strong parentName="p">{`left`}</strong>{` of the submit button.`}</p>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={["column", "column", "column", "column", "row"]} sx={{
      gap: 3
    }} mdxType="Box">
    <img width="456" alt="A small form in a dialog that has 'Cancel' and 'Save' buttons at the bottom right" src="https://user-images.githubusercontent.com/2313998/151623724-d59d5967-5484-4e81-a4c2-1109409956ee.png" />
    <Box as="p" mt="0" mdxType="Box">If the save button is used in a dialog, place the button at the bottom right of the dialog. Right-aligned dialog buttons feel familiar because it's a common pattern across various platforms such as Windows, macOS, and Android.</Box>
    </Box>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={["column", "column", "column", "column", "row"]} sx={{
      gap: 3
    }} mdxType="Box">
    <img width="456" alt="A multi-line text input with 'Close with Comment' and 'Comment' buttons right-aligned below the input" src="https://user-images.githubusercontent.com/2313998/158623777-a4bb25ec-e1a0-4feb-bd56-e46a69c7f768.png" />
    <Box as="p" mt="0" mdxType="Box">
        If the button is used to send a message such as a comment on an issue, right-align the button below the text area. This is where GitHub has traditionally placed the <strong>Submit</strong> button for issues and pull requests, and it's common practice in other apps to place a <strong>Send</strong> button to the right.
    </Box>
    </Box>
    {
      /*
      TODO:
      Consider how we might define more patterns to keep the "Save" button easily accessible on long forms:
      - add save and cancel buttons on top AND bottom?
      - use a sticky header with save and cancel buttons?
      */
    }
    <h5>{`Adjacent to inline editing input`}</h5>
    <p>{`To simplify the interface, some content may be edited inline without taking the user to a separate flow.`}</p>
    <p>{`An input used for inline editing should have a save button placed very close to visually connect the input with the button.`}</p>
    <img width="960" alt="Two images of a pull request header. The first image shows the title as read-only text. The second image shows a text input to edit the pull request title with 'Cancel' and 'Save' buttons below the input." src="https://user-images.githubusercontent.com/2313998/158624334-fcd4e268-d144-4420-98ed-819c51631e7e.png" />
    <h5>{`Other`}</h5>
    <p>{`If you believe your submit button will not be intuitive or easily accessible using any of the placement options listed here, you may consider alternatives and optionally propose a new Primer pattern.`}</p>
    <h2>{`Automatic saving`}</h2>
    <p>{`Automatic saving applies changes as they are made. Automatic saving should be used when the user expects instant feedback from the change they made.`}</p>
    <Box mb={3} display="flex" alignItems="center" flexDirection="column" mt={6} mx="auto" sx={{
      gap: 2,
      maxWidth: '450px'
    }} mdxType="Box">
    <Box display="inline-block" overflow="hidden" borderWidth="1px" borderColor="border.default" borderStyle="solid" borderRadius="12px" mdxType="Box">
        <img width="456" src="https://user-images.githubusercontent.com/2313998/152013139-02eb5213-bb19-4c8d-8b19-01f1d73602d1.gif" alt="Opening the 'Watch' dropdown menu and changing. It saves automatically." />
    </Box>
    <Text as="p" mt="0" align="center" fontSize={1} mdxType="Text">If a form field is saved automatically, it should be obvious whether or not it saved without using text or visual indicators. A visual indicator for whether or not a form field has been saved successfully could be mistaken as a validation status.</Text>
    </Box>
    <h3>{`Imperative controls`}</h3>
    <p>{`To reduce UI and give instant feedback, you should use automatic saving (and not explicit saving) for imperative controls. These are:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/toggle-switch"
        }}>{`Toggle switches`}</a>{`. The toggle switch behaves like a light switch: the light just switches on and off without you having to confirm each time you flip the switch.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/segmented-control"
        }}>{`Segmented controls`}</a>{`. The segmented control behaves like tabs: the tab changes as soon as you select it, and you can only select one at a time.`}</li>
      <li parentName="ul">{`Single-select dropdowns that are not native `}<inlineCode parentName="li">{`<select>`}</inlineCode>{` dropdowns or part of an explicitly saved form. Single-select dropdowns behave like a radio dial: the station starts playing as soon as you select it.`}</li>
    </ul>
    <h3>{`Dropdown menu accessibility`}</h3>
    <p>{`Semantic menus should only have a list of options to select from and should not have a save button in their dropdown. As soon as additional features (search inputs, filters, tabs, etc) are added, the component should be treated like a dialog with a button to submit and a button to cancel. `}</p>
    <h2>{`Feedback`}</h2>
    <p>{`If the change is not obvious within the user's viewport, provide feedback to let the user know that the change occurred successfully.`}</p>
    <ul>
      <li parentName="ul">{`If the change occurred without a redirect or page refresh, you can use the `}<a parentName="li" {...{
          "href": "https://primer.style/design/ui-patterns/messaging#toasts"
        }}>{`toast component`}</a>{`.`}</li>
      <li parentName="ul">{`If the change occurred and the page refreshes or redirects, you can use a `}<a parentName="li" {...{
          "href": "https://primer.style/design/ui-patterns/messaging#flash-alerts"
        }}>{`flash banner component`}</a>{`.`}</li>
    </ul>
    <Note variant="warning" mdxType="Note">If your design is being implemented in github.com, avoid using a toast for now. The toast components used on github.com have accessibility issues that need to be fixed.</Note>
    <h2>{`Error forgiveness`}</h2>
    <p>{`Before submitting a form with potentially destructive consequences (for example, delete a repository), ask the user to confirm they want to submit the form.`}</p>
    <p>{`For risky changes, provide a way to undo those changes after saving.`}</p>
    <Box display="flex" alignItems="center" flexDirection="column" mx="auto" sx={{
      gap: 1
    }} mdxType="Box">
    <Box display="inline-block" overflow="hidden" borderWidth="1px" borderColor="border.default" borderStyle="solid" borderRadius="12px" mdxType="Box">
        <img width="960" src="https://user-images.githubusercontent.com/2313998/152434199-a067b037-baf8-4093-8796-b0bb58344223.png" />
    </Box>
    <Text as="p" mt="0" align="center" fontSize={1} mdxType="Text">After merging a pull request, a button appears to revert the changes</Text>
    </Box>
    <h2>{`Redirecting`}</h2>
    <p>{`When creating new content like issues, discussions, or even larger entities such as repositories or organizations, you may redirect the user to the entity they just created at the end of the flow.`}</p>
    <p>{`In some cases, such as forking or using a template, you may need to hold the user at a waiting page while the entity is generated. In these cases, make the user feel confident that something is happening and that their configuration details will not be lost. `}</p>
    <Box overflow="hidden" borderWidth="1px" borderColor="border.default" borderStyle="solid" borderRadius="12px" mdxType="Box">
    <img src="https://user-images.githubusercontent.com/2313998/151446009-58bfb92f-00e1-4a15-a2a3-f1afb6fb4031.gif" alt="Screen recording of a user forking a repository" />
    </Box>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      